"use client";

import React from "react";
import Link from "next/link";
import { ArrowRight, Sparkles, Printer, LayoutGrid, Hammer, ChevronRight } from "lucide-react";
import { USLUGI_MEGA_MENU } from "./nav-data";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenOrderModal: (service?: string) => void;
}

const columnIcons = [
  Sparkles,
  Printer,
  LayoutGrid,
  Hammer,
];

export function MegaMenu({ isOpen, onClose, onOpenOrderModal }: MegaMenuProps) {
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    onClose();
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      if (href.startsWith("/uslugi/")) {
        const servicesSection = document.getElementById("services");
        if (servicesSection) {
          e.preventDefault();
          servicesSection.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(null, "", href);
        }
      }
    }
  };

  return (
    <div
      className={`absolute left-0 right-0 top-full pt-2 z-50 transition-all duration-200 ease-out origin-top ${isOpen
        ? "opacity-100 translate-y-0 pointer-events-auto visible"
        : "opacity-0 -translate-y-2 pointer-events-none invisible"
        }`}
      role="region"
      aria-label="Меню Услуги и продукция"
    >
      <div className="absolute -top-3 left-0 right-0 h-4 bg-transparent" />

      <div className="relative mx-auto max-w-7xl rounded-2xl border border-gray-200/90 dark:border-zinc-800/90 bg-white/98 dark:bg-[#0c0f18]/98 backdrop-blur-2xl shadow-xl shadow-slate-900/10 dark:shadow-black/60 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7 p-6 sm:p-7">
          {USLUGI_MEGA_MENU.map((block, idx) => {
            const Icon = columnIcons[idx] || Sparkles;

            return (
              <div
                key={block.id}
                className="flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-zinc-800/60 pb-6 lg:pb-0 lg:pr-6 last:border-none last:pr-0"
              >
                <div>
                  <Link
                    href={block.href}
                    onClick={(e) => handleLinkClick(e, block.href)}
                    className="group flex items-start gap-3 mb-4 select-none"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0a3d90]/10 to-[#55a9ef]/15 dark:from-[#0a3d90]/30 dark:to-[#55a9ef]/30 text-[#0a3d90] dark:text-[#55a9ef] flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-bold tracking-tight text-gray-950 dark:text-white group-hover:text-[#0a3d90] dark:group-hover:text-[#55a9ef] transition-colors">
                          {block.title}
                        </h3>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-zinc-400 line-clamp-1">
                        {block.tagline}
                      </p>
                    </div>
                  </Link>

                  {block.directItems && block.directItems.length > 0 && (
                    <ul className="space-y-1 mb-3">
                      {block.directItems.map((item) => (
                        <li key={item.href}>
                          <Link
                            href={item.href}
                            onClick={(e) => handleLinkClick(e, item.href)}
                            className="group flex items-center justify-between py-1 px-2 rounded-md text-[13px] font-medium text-gray-700 dark:text-zinc-300 hover:text-[#0a3d90] dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-zinc-800/60 transition-all duration-150"
                          >
                            <span className="transition-transform duration-150 group-hover:translate-x-0.5">
                              {item.title}
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#55a9ef]" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  {block.subGroups && (
                    <div className="space-y-4 pt-1">
                      {block.subGroups.map((sub) => (
                        <div
                          key={sub.title}
                          className="pt-2 border-t border-gray-100 dark:border-zinc-800/60"
                        >
                          <div className="flex items-center justify-between mb-1.5 px-2">
                            <Link
                              href={sub.href}
                              onClick={(e) => handleLinkClick(e, sub.href)}
                              className="text-[12px] font-semibold text-gray-900 dark:text-zinc-200 hover:text-[#0a3d90] dark:hover:text-[#55a9ef] transition-colors"
                            >
                              {sub.title}
                            </Link>
                            {sub.badge && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-pink-500/10 text-[#FF2E93] font-medium">
                                {sub.badge}
                              </span>
                            )}
                          </div>

                          <ul className="space-y-0.5">
                            {sub.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  href={item.href}
                                  onClick={(e) => handleLinkClick(e, item.href)}
                                  className="group flex items-center justify-between py-1 px-2 rounded-md text-[12.5px] text-gray-600 dark:text-zinc-400 hover:text-[#0a3d90] dark:hover:text-white hover:bg-gray-100/70 dark:hover:bg-zinc-800/50 transition-all duration-150"
                                >
                                  <span className="transition-transform duration-150 group-hover:translate-x-0.5">
                                    {item.title}
                                  </span>
                                  <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#55a9ef]" />
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-100 dark:border-zinc-800 bg-gray-50/80 dark:bg-zinc-900/60 px-6 sm:px-7 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3 text-gray-600 dark:text-zinc-400">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Собственный производственный цех в Сыктывкаре</span>
          </div>

          <div className="flex items-center gap-4">
            <Link
              href="/uslugi/"
              onClick={(e) => handleLinkClick(e, "/uslugi/")}
              className="font-semibold text-[#0a3d90] dark:text-[#55a9ef] hover:underline flex items-center gap-1"
            >
              <span>Все услуги каталога</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>

            <button
              onClick={() => {
                onClose();
                onOpenOrderModal("Быстрый расчет услуг");
              }}
              className="px-3.5 py-1.5 rounded-full font-medium text-white bg-gradient-to-r from-[#0a3d90] to-[#55a9ef] hover:from-[#083070] hover:to-[#4293d8] transition-all shadow-sm"
            >
              Рассчитать заказ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
